import {
  Placement,
  autoPlacement,
  autoUpdate,
  offset,
  size,
  useFloating,
} from "@floating-ui/react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import { FormControlSize, LoadingDots, useBreakpoint } from "cy-fe";
import { Nil, noop } from "cy-util";
import { CheckIcon, SearchIcon, XIcon } from "lucide-react";
import {
  ButtonHTMLAttributes,
  ReactNode,
  forwardRef,
  useEffect,
  useState,
  useCallback,
  PointerEvent,
  MouseEvent,
  KeyboardEvent,
} from "react";
import { flushSync } from "react-dom";
import { cn } from "cy-fe/src/utils/cn";
import { formControlVariants } from "cy-fe/src/components/Forms/classes";

export function ComboBox<TValue>({
  value,
  id,
  onChange,
  buttonClassName,
  buttonChildren,
  panelClassName,
  getOptions,
  renderItems,
  allowedPlacements,
  loading,
  error,
  disabled,
  buttonSize = "lg",
  useAvailableWidth,
  maxHeight: overrideMaxHeight,
  ignoreCalculatedWidth,
  label,
  canClear,
  "data-testid": dataTestId,
}: {
  canClear?: boolean;
  id?: string;
  label?: string;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
  buttonSize?: FormControlSize;
  ignoreCalculatedWidth?: boolean;
  maxHeight?: number;
  renderItems: (options: TValue[]) => ReactNode;
  allowedPlacements?: Placement[];
  value: TValue | null;
  onChange: (val: TValue | null) => void;
  getOptions: (query: string) => TValue[];
  panelClassName?: string;
  buttonClassName?: string | ((val: TValue | Nil) => string);
  buttonChildren: (val: TValue | Nil) => ReactNode;
  useAvailableWidth?: boolean;
  "data-testid"?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [maxHeight, setMaxHeight] = useState<number>();
  const [width, setWidth] = useState<number>();
  const bp = useBreakpoint();
  const isFloating = bp.hasMinWidth("sm");
  const baseTestId = dataTestId || "combobox";

  // seems theres some issue in headless ui with floating ui.
  // control doesnt focus on open.
  const autofocusRef = useCallback((e: any) => {
    if (e) {
      setTimeout(() => e.focus(), 0);
    }
  }, []);

  function getButtonClassName(val: TValue | Nil) {
    if (typeof buttonClassName === "function") {
      return buttonClassName(val);
    }
    return buttonClassName || "";
  }

  function clearHandler(e: MouseEvent<HTMLElement>) {
    e.preventDefault();
    e.stopPropagation();
    onChange(null);
  }
  function clearKeyHandler(e: KeyboardEvent<HTMLSpanElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onChange(null);
    }
  }
  const { refs, floatingStyles } = useFloating({
    strategy: "absolute",
    open: open && isFloating,
    middleware: [
      autoPlacement({
        allowedPlacements: allowedPlacements || ["bottom", "top"],
      }),
      offset(5),
      size({
        apply({ availableHeight, availableWidth, rects }) {
          flushSync(() => {
            const targetHeight = availableHeight - 10;
            setMaxHeight(
              !!overrideMaxHeight && overrideMaxHeight < targetHeight
                ? overrideMaxHeight
                : targetHeight,
            );
            if (useAvailableWidth) {
              setWidth(availableWidth);
            } else {
              setWidth(rects.reference.width + 4);
            }
          });
        },
      }),
    ],
    whileElementsMounted: open && isFloating ? autoUpdate : undefined,
  });
  const filteredOptions = getOptions(query);

  const inputCss = cn(
    formControlVariants({
      vSize: "md",
    }),
    "mx-auto w-[calc(100%_-_1rem)] mb-3 mt-4 h-12 pl-10 leading-5 ",
  );
  const buttonCss = cn(formControlVariants({ vSize: buttonSize }));

  function renderOptions() {
    return (
      <Transition
        afterEnter={() => setQuery("")}
        afterLeave={() => setQuery("")}
        as={"div"}
        className="shadow-popover border-popover-outline z-20 flex h-full max-h-[inherit] w-full flex-col rounded-lg border bg-white pb-1 sm:rounded"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        {!isFloating && (
          <ClosePopupButton
            onClose={() => (refs.reference.current as any)?.focus()}
          />
        )}
        <div className="relative flex items-center">
          <ComboboxInput
            ref={autofocusRef}
            autoComplete={"off"}
            autoFocus
            placeholder="Search"
            data-testid={`${baseTestId}-input`}
            className={inputCss}
            onChange={(event) => setQuery(event.target.value)}
          />
          <SearchIcon className="absolute left-5 h-5 w-5 stroke-black" />
        </div>
        <ComboboxOptions
          className="options-panel h-full w-full overflow-auto"
          data-testid={`${baseTestId}-dropdown`}
        >
          <OpenTracker open={open} callback={setOpen} />
          <div className={cn("relative cursor-pointer px-2 py-1")}>
            {renderItems(filteredOptions)}
          </div>
        </ComboboxOptions>
      </Transition>
    );
  }

  return (
    <Combobox
      onChange={(x) => (x != null ? onChange(x) : noop())}
      value={value}
      disabled={disabled}
    >
      <ComboboxButton
        id={id}
        as={ComboButton}
        type="button"
        role="combobox"
        aria-label={label}
        data-testid={baseTestId}
        className={cn(
          "flex w-max items-center gap-3",
          buttonCss,
          "w-full px-3 py-0 pr-8",
          "relative",
          error && "!ring-red-500",
          !disabled && "hover:ring-2 focus:ring-[3px]",
          (disabled || loading) && "bg-gray-100",
          getButtonClassName(value),
        )}
        onClick={() => setOpen(true)}
        ref={refs.setReference}
      >
        {buttonChildren(value)}
        {loading && <LoadingDots height={12} className="absolute right-3" />}
        {!loading && canClear && value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear selection"
            onClick={clearHandler}
            onKeyDown={clearKeyHandler}
            className="absolute right-2 rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <XIcon />
          </span>
        )}
      </ComboboxButton>
      {isFloating ? (
        <div
          className={cn(
            "z-20",
            "absolute",
            panelClassName,
            "flex flex-col",
            !open && "pointer-events-none hidden",
          )}
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            maxHeight,
            ...(ignoreCalculatedWidth ? {} : { width }),
          }}
        >
          {" "}
          {renderOptions()}
        </div>
      ) : null}
      {!isFloating && (
        <div
          className={cn(
            "z-20",
            "fixed inset-2 top-5 bottom-0",
            panelClassName,
            "w-none max-w-none",
            !open && "pointer-events-none hidden",
          )}
        >
          {" "}
          {renderOptions()}
        </div>
      )}
    </Combobox>
  );
}

function ClosePopupButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      onClick={onClose}
      type="button"
      inert={false}
      className="mt-5 mr-5 ml-auto cursor-pointer rounded-full p-2 hover:bg-gray-100"
    >
      <XIcon />
    </button>
  );
}

export function ComboOption({
  active,
  selected,
  children,
  className,
}: {
  active: boolean;
  selected: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between rounded border border-transparent px-5 py-2",
        active && "border-black",
        selected ? "font-semibold" : "font-normal",
        className,
      )}
    >
      {children}
      {selected ? <CheckIcon className="stroke-2 text-green-700" /> : null}
    </div>
  );
}

export function renderSelectOptions(
  options: {
    prefix?: string;
    label: string;
    key: string | number;
    value: unknown;
  }[],
) {
  return (
    <div>
      {" "}
      {options.map((opt, index) => (
        <ComboboxOption
          className="mx2 py-1"
          order={index}
          value={opt.value}
          key={opt.key}
        >
          {({ focus, selected }) => (
            <ComboOption active={selected || focus} selected={selected}>
              <div className="flex">
                {!!opt.prefix && (
                  <div className="w-16 font-semibold">{opt.prefix}</div>
                )}
                <div>{opt.label}</div>
              </div>
            </ComboOption>
          )}
        </ComboboxOption>
      ))}
    </div>
  );
}

// Need to detect from inner element when its closed
// so we can disable floating ui tracking on parent.
// maybe the box shoud be controlled.
function OpenTracker({
  callback,
}: {
  open: boolean;
  callback: (open: boolean) => void;
}) {
  useEffect(() => {
    callback(true);
    return () => callback(false);
  }, []);
  return null;
}

type ComboButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;
const ComboButton = forwardRef<HTMLButtonElement, ComboButtonProps>(
  function ComboButton(
    { onClick, onPointerDown, ...props }: ComboButtonProps,
    ref,
  ) {
    // this is a hack to revert the library hack that uses onPointerDown instead of onClick as the trigger
    // On pointer down will trigger on drag. On click wont.
    //
    // https://github.com/tailwindlabs/headlessui/issues/3793
    //
    function clickHandler(e: PointerEvent<HTMLButtonElement>) {
      if (onPointerDown != null) {
        onPointerDown(e);
      }
    }
    return (
      <button
        onClick={clickHandler}
        {...props}
        ref={ref}
        tabIndex={undefined}
      />
    );
  },
);

export function createFilterOptionsFn<T>(textFn: (obj: T) => string) {
  return (query: string, options: T[]) => {
    return options.filter((x) =>
      textFn(x).toLowerCase().includes(query.toLowerCase()),
    );
  };
}
